import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import FlashHotelRateOption from './FlashHotelRateOption'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { useAppSelector } from 'hooks/reduxHooks'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import { getDynamicPricingUrgencyLabelData } from '../utils'
import { useInView } from 'react-intersection-observer'
import config from 'constants/config'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { useUrgentOfferRoomDateData } from 'hooks/Offers/useUrgentOfferRoomDateData'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(SmallToggleButton)`
  padding: ${rem(12)};
  width: 100%;
  justify-content: center;
`

interface Props {
  offer: App.Offer
  roomTypeId: string
  packageViews: Array<App.OfferPackageOptionView>
  flightDestination?: App.Airport
  previewMode: boolean
  checkIn?: string
  availableRoomCount?: number
  availableForPurchase?: boolean
}

const initialRatesToShow = 4

function FlashHotelRoomOption(props: Props) {
  const {
    offer,
    roomTypeId,
    packageViews,
    flightDestination,
    previewMode,
    checkIn,
    availableRoomCount,
    availableForPurchase,
  } = props

  const offerId = offer.id

  const cachedRoomDateData = useUrgentOfferRoomDateData(offerId, roomTypeId)

  const pkg = packageViews[0].package

  const liveRoomsBeforeSurcharge = useMemo(() => getDynamicPricingUrgencyLabelData(cachedRoomDateData, packageViews[0].availableRooms), [cachedRoomDateData, packageViews])
  const urgencyTagAbTestVariation = useAppSelector(state => getOptimizelyExperimentVariation(state, OptimizelyExperiments.lereDynamicPricingUrgencyTag))
  const [sectionRef, sectionInView] = useInView({ triggerOnce: false, threshold: 0.4 })
  const canTriggerExperiment = useMemo(() => {
    return sectionInView && !!liveRoomsBeforeSurcharge && config.BRAND === 'luxuryescapes'
  }, [sectionInView, liveRoomsBeforeSurcharge])
  useOptimizelyExperiment(OptimizelyExperiments.lereDynamicPricingUrgencyTag, canTriggerExperiment)

  const [experimentHideRoomsRemainingLabel, experimentRoomsBeforeSurcharge] = useMemo(() => {
    if (urgencyTagAbTestVariation === 'on') {
      return [!!liveRoomsBeforeSurcharge, liveRoomsBeforeSurcharge]
    }
    return [false, undefined]
  }, [urgencyTagAbTestVariation, liveRoomsBeforeSurcharge])

  const [showingMoreRates, showMoreRates] = useToggle()

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const availablePackageViews = useMemo(() => packageViews.filter(view => view.capacityValid && view.hotelAvailable), [packageViews])
  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const rateOptionViews = previewMode ? packageViews : availablePackageViews

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (availableForPurchase) {
      if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
      if (!datesAvailable) return 'sold-out-on-dates'
    } else {
      return 'sold-out'
    }
  }, [availableForPurchase, datesAvailable, roomFitsCapacity])

  const offerPageDispatch = useOfferPageDispatch()

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, pkg.roomType])

  const handleChangeDatesClick = useCallback(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  if (!pkg.roomType) { return null }

  return (
    <Pane ref={sectionRef} border="neutral-five" borderRadius="S" data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetails
          roomPackage={pkg}
          urgencyLabels={<>
            {!datesAvailable && <UrgencyLabel variant="critical2">'SOLD OUT'</UrgencyLabel>}
            <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
            {checkIn && !croPopularOptionBookingRemaining && !experimentHideRoomsRemainingLabel && <RoomsRemainingLabel roomCount={packageViews[0].availableRooms ?? 0} threshold="rate" />}
            {checkIn && croPopularOptionBookingRemaining && !experimentHideRoomsRemainingLabel && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} /> }
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
        >
          <AccommodationRoomTileActions
            roomPackage={pkg}
            state={actionsState}
            onChangeOccupancyClick={onChangeOccupancyClick}
            onChangeDatesClick={handleChangeDatesClick}
          />
        </AccommodationRoomTileDetails>
      </AccommodationRoomTile>
      <RateSection>
        {take(rateOptionViews, showingMoreRates ? 100 : initialRatesToShow).map((view, index) => <FlashHotelRateOption
          key={view.package.uniqueKey}
          roomsBeforeSurcharge={experimentRoomsBeforeSurcharge}
          offer={offer}
          optionNumber={index + 1}
          packageView={view}
          flightDestination={flightDestination}
          showOptionLabel={rateOptionViews.length > 1}
        />)}
        {rateOptionViews.length > initialRatesToShow &&
          <ShowMoreContent
            on={showingMoreRates}
            onClick={showMoreRates}
            size="small"
          >
            {showingMoreRates ? 'Show less options' : 'Show more options'}
          </ShowMoreContent>}
      </RateSection>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  previewMode: appState.system.previewMode,
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), FlashHotelRoomOption, preventRenderWithOpenFilter)
